/**
 * Afrikaans: Inbox
 *
 * What this file covers: Conversations with clients on every channel: replying, notes, assigning, taking over
 * from the chatbot, canned replies.
 *
 * Tone: Quick and practical, for people answering clients all day. Short labels. When something cannot be sent,
 * say why and what to do, in one sentence.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const inbox: Catalog = {
  'inbox.title': 'Inkassie',
  'inbox.assignedTo': 'Toegewys aan {{name}}.',
  'inbox.unassignedToast': 'Gesprek se toewysing verwyder.',
  'inbox.resolvedToast': 'Gesprek opgelos.',
  'inbox.reopenedToast': 'Gesprek heropen.',
  'inbox.formerAgent': 'ŉ Spanlid wat vertrek het',
  'inbox.context.title': 'Kontak',
  'inbox.context.open': 'Kontak',
  'inbox.context.notInBook': 'Nog nie in jou kontakboek nie.',
  'inbox.context.addContact': 'Voeg by kontakte',
  'inbox.context.handoffs': 'Oorhandigings',
  'inbox.context.handoff': '{{from}} → {{to}}',
  'inbox.empty.hint': 'Heg ŉ WhatsApp-nommer aan en gesprekke verskyn hier soos mense jou boodskap.',
  'inbox.empty.action': 'Heg ŉ nommer aan',
  'inbox.empty.filtered': 'Niks pas by hierdie filters nie',
  'inbox.empty.filtered.hint': 'Probeer ŉ ander tou, toestand of soekterm.',
  'inbox.filters.clear': 'Vee filters uit',
  'inbox.filters.queue': 'Tou',
  'inbox.filters.state': 'Toestand',
  'inbox.filters.state.any': 'Enige toestand',
  'inbox.queue.all': 'Almal',
  'inbox.queue.mine': 'Myne',
  'inbox.queue.unassigned': 'Nie toegewys nie',
  'inbox.search.placeholder': 'Soek naam, nommer of boodskap',
  'inbox.search.label': 'Soek gesprekke',
  'inbox.list.label': 'Gesprekke',
  'inbox.loadError': 'Gesprekke kon nie gelaai word nie. Dit beteken nie jy het geen nie.',
  'inbox.loadMore': 'Laai meer',
  'inbox.loadMore.of': 'Laai meer ({{shown}} van {{total}})',
  'inbox.jumpToLatest': 'Spring na nuutste',
  'inbox.unread.count': '{{count}} ongelees',
  'inbox.messages.empty': 'Nog geen boodskappe nie',
  'inbox.messages.empty.hint': 'Niks is op hierdie gesprek gestuur of ontvang nie.',
  'inbox.messages.error': 'Hierdie gesprek kon nie gelaai word nie.',
  'inbox.message.sending': 'Stuur tans…',
  'inbox.message.delivered': 'Afgelewer',
  'inbox.message.read': 'Gelees',
  'inbox.message.failed': 'Nie afgelewer nie',
  'inbox.message.failed.code': 'Nie afgelewer nie (kode {{code}})',
  'inbox.send.noCredits': 'Nie genoeg krediet om te stuur nie. Laai die beursie op om voort te gaan.',
  'inbox.send.numberInactive': 'Daardie WhatsApp-nommer is nie meer aktief nie.',
  'inbox.resolve.confirm.title': 'Los op met ongelese boodskappe?',
  'inbox.resolve.confirm.body': 'Hierdie gesprek het nog {{count}} ongelees. Om dit op te los haal dit uit die tou.',
  'inbox.window.closed': 'Antwoordvenster gesluit',
  'inbox.window.closed.tooltip': 'Meer as 24 uur sedert hierdie persoon se laaste boodskap. WhatsApp aanvaar nou net ŉ goedgekeurde sjabloon.',
  'inbox.window.closed.title': 'Die 24-uur antwoordvenster het gesluit',
  'inbox.window.closed.body': 'WhatsApp aanvaar net ŉ goedgekeurde sjabloon meer as 24 uur nadat iemand laas geskryf het. Stuur ŉ sjabloon om die gesprek te heropen.',
  'inbox.window.closed.action': 'Gaan na sjablone',
  'inbox.window.open.tooltip': 'Hoe lank jy nog in jou eie woorde kan antwoord voordat net sjablone aanvaar word.',
  'inbox.window.openHours': '{{hours}}u {{minutes}}m oor om te antwoord',
  'inbox.window.openMinutes': '{{minutes}}m oor om te antwoord',
  'inbox.description': 'Regstreekse WhatsApp-gesprekke. Antwoorde binne die 24-uur-venster gaan as vryevorm-teks uit.',
  'inbox.empty': 'Nog geen gesprekke nie: heg ’n nommer aan en laat ’n ontvanger vir jou boodskap stuur.',
  'inbox.select': 'Kies ’n gesprek om die geskiedenis te lees en te antwoord.',
  'inbox.noMessages': 'Nog geen boodskappe nie',
  'inbox.loadingMessages': 'Laai boodskappe…',
  'inbox.assign': 'Ken agent toe',
  'inbox.unassigned': 'Nie toegeken nie',
  'inbox.assigned': 'Toekenning bygewerk.',
  'inbox.lock': 'Sluit',
  'inbox.locked': 'Gesprek aan jou gesluit.',
  'inbox.lockedBy': 'Gesluit deur',
  'inbox.anotherAgent': '’n ander agent',
  'inbox.send': 'Stuur',
  'inbox.reply.placeholder': 'Tik ’n antwoord… (/ vir blikantwoorde)',
  'inbox.newline': 'vir ’n nuwe reël',
  'inbox.canned.hint': 'voeg ’n blikantwoord in',
  'inbox.canned.noMatch': 'Geen blikantwoorde pas nie.',
  'inbox.canned.manage': 'Bestuur blikantwoorde',
  'inbox.canned.navigate': 'navigeer',
  'inbox.canned.insert': 'voeg in',
  'inbox.canned.close': 'sluit',
  'inbox.tags.title': 'Etikette',
  'inbox.tags.add': 'Voeg etiket by',
  'inbox.tags.placeholder': 'Etiketnaam…',
  'inbox.tags.suggestions': 'Bestaande etikette',
  'inbox.transfer': 'Dra oor',
  'inbox.transfer.title': 'Dra gesprek oor',
  'inbox.transfer.agent': 'Dra oor aan',
  'inbox.transfer.note': 'Nota vir die ontvangende agent (opsioneel)',
  'inbox.transfer.noteprefix': 'Oorgedra aan',
  'inbox.transfer.keeps': 'Notas en etikette bly by die gesprek.',
  'inbox.transfer.done': 'Gesprek oorgedra.',
  'inbox.transfer.noAgents': 'Niemand anders is nog in jou span nie. Nooi mense uit onder Instellings, Span.',
  'inbox.sla.firstReply': 'Eerste antwoord',
  'inbox.sla.resolve': 'Los op',
  'inbox.sla.left': 'oor',
  'inbox.sla.over': 'oortyd',
  'inbox.sla.breached': 'SLA oortree',
  'inbox.sla.met': 'SLA nagekom',
  'inbox.sla.due': 'Verval',
  'inbox.notes.title': 'Interne notas',
  'inbox.notes.empty': 'Nog geen notas nie.',
  'inbox.notes.agent': 'Agent',
  'inbox.notes.placeholder': 'Voeg ’n interne nota by (word nie aan die kontak gestuur nie)…',
  'inbox.notes.add': 'Voeg nota by',
  'inbox.lock.heldBy': 'Gesluit deur',
  'inbox.lock.until': 'tot',
  'inbox.lock.mine': 'Jy hou hierdie gesprek',
  'inbox.lock.release': 'Laat los',
  'inbox.lock.released': 'Slot losgelaat.',
  'inbox.lock.readOnly': 'Slegs-lees terwyl ’n ander agent antwoord.',
  'inbox.resolve': 'Los op',
  'inbox.reopen': 'Heropen',
  'inbox.stateChanged': 'Gesprek bygewerk.',
  'inbox.state.pending': 'Hangend',
  'inbox.state.open': 'Oop',
  'inbox.state.resolved': 'Opgelos',
  'inbox.state.archived': 'Geargiveer',
  'canned.title': 'Blikantwoorde',
  'canned.description': 'Herbruikbare antwoorde wat jou agente invoeg deur "/" in die inkassie-opsteller te tik.',
  'canned.new': 'Nuwe antwoord',
  'canned.edit': 'Wysig antwoord',
  'canned.shortcut': 'Kortpad',
  'canned.shortcutHint': 'Kleinletters, syfers, punte, koppeltekens of onderstrepe: agente tik /kortpad.',
  'canned.titleField': 'Titel',
  'canned.body': 'Boodskap',
  'canned.bodyHint': 'Word presies soos geskryf gestuur (maks. 4096 karakters).',
  'canned.language': 'Taal',
  'canned.search': 'Soek kortpaaie of titels…',
  'canned.empty': 'Nog geen blikantwoorde nie.',
  'canned.emptyHint': 'Skep een en agente kan dit met "/" in die inkassie invoeg.',
  'canned.shortcutTaken': 'Daardie kortpad is reeds in gebruik.',
  'canned.invalid': 'Kortpad en boodskap is verpligtend.',
  'canned.saved': 'Blikantwoord gestoor.',
  'canned.created': '/{{shortcut}} geskep.',
  'canned.discard.title': 'Gooi jou veranderinge weg?',
  'canned.discard.body': 'Jy het ongestoorde wysigings aan hierdie antwoord.',
  'canned.discard.confirm': 'Gooi hulle weg',
  'canned.discard.keep': 'Hou aan wysig',
  'canned.language.any': 'Enige taal',
  'canned.deleted': 'Blikantwoord geskrap.',
  'canned.deleteConfirm': 'Skrap hierdie blikantwoord? Agente kan dit nie meer invoeg nie.',
  'canned.updated': 'Bygewerk',
  'inbox.assignFailed': 'Toekenning het misluk',
  'inbox.cannedReplies.keyEnter': 'Enter',
  'inbox.cannedReplies.keyEsc': 'Esc',
  'inbox.keyShift': 'Shift',
  'inbox.lockFailed': 'Sluit het misluk',
  'inbox.notes.addFailed': 'Kon nie nota byvoeg nie.',
  'inbox.releaseFailed': 'Loslaat het misluk',
  'inbox.sendFailed': 'Stuur het misluk',
  'inbox.tags.addFailed': 'Kon nie etiket byvoeg nie.',
  'inbox.tags.removeFailed': 'Kon nie etiket verwyder nie.',
  'inbox.transfer.transferFailed': 'Oordrag het misluk',
  'inbox.updateFailed': 'Bywerk het misluk',
  'inbox.conversations': 'Gesprekke',
  'inbox.filters.more': 'Meer filters',
  'inbox.queue.open': 'Oop',
  'inbox.queue.resolved': 'Opgelos',
  'inbox.showingOf': 'Wys {{shown}} van {{total}}',
  'inbox.sla': 'SLA',
  'inbox.tags': 'Etikette',
  'inbox.assignedTo.label': 'Toegewys aan',
  'inbox.composer.emoji': 'Voeg ’n emoji in',
  'inbox.composer.canned': 'Voeg ’n vooraf-antwoord in',
  'inbox.composer.hint': 'Druk Enter om te stuur, Shift+Enter vir ’n nuwe reël',
  'inbox.message.sent': 'Gestuur',
  'inbox.notes.private': 'Privaat',
  'inbox.quickReplies.title': 'Vinnige antwoorde',
  'inbox.quickReplies.search': 'Soek antwoorde',
  'inbox.quickReplies.empty': 'Nog geen vooraf-antwoorde nie.',
  'inbox.tip': 'Wenk: tik / in die antwoordblok vir vooraf-antwoorde, en druk Enter om te stuur.',
  'inbox.sla.onTrack': 'Op koers',
  'inbox.sla.dueSoon': 'Binnekort due',
  'inbox.replyWindow': 'Antwoordvenster',
  'inbox.attachment.open': 'Bekyk',
  'inbox.attachment.unavailable': 'Die lêer kon nie bewaar word toe dit aangekom het nie.',
  'inbox.attachment.kind.image': 'Foto',
  'inbox.attachment.kind.video': 'Video',
  'inbox.attachment.kind.audio': 'Stemnota',
  'inbox.attachment.kind.document': 'Dokument',
  'inbox.attachment.kind.sticker': 'Plakker',
  'inbox.spend': 'Bestee aan hierdie kontak',
  'inbox.spendHint': '{{count}} heffings: boodskappe, botantwoorde en modeltekens. Maak gebruik oop.',
  'inbox.newMessage': 'Nuwe boodskap',
  'inbox.message.failed.WINDOW_CLOSED': 'Nie afgelewer nie: hulle het nie in 24 uur aan jou geskryf nie, dus was \'n goedgekeurde sjabloon hiervoor nodig.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE': 'Nie afgelewer nie: WhatsApp het dit afgekeur weens \'n faktureringsprobleem aan ons kant, nie joune nie. Ons maak dit reg.',
  'inbox.message.failed.UNDELIVERABLE': 'Nie afgelewer nie: WhatsApp kon dit nie by daardie nommer aflewer nie.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED': 'Nie afgelewer nie: die nommer waarvandaan jy gestuur het, is nie by WhatsApp geregistreer nie.',
  'inbox.message.failed.MEDIA_NOT_FETCHED': 'Nie afgelewer nie: WhatsApp kon nie die aangehegte lêer haal nie.',
  'inbox.message.failed.META_REFUSED': 'Nie afgelewer nie: WhatsApp het dit aanvaar en dit toe nie afgelewer nie.',
  'inbox.message.failed.notCharged': 'Jy is nie hiervoor gehef nie.',
  'inbox.message.template.marker': 'Sjabloon · {{name}}',
  'inbox.message.template.noWording': 'Die bewoording van hierdie sjabloonboodskap is nie aangeteken toe dit gestuur is nie.',
  'inbox.message.template.noWording.named': 'Die bewoording van “{{name}}” is nie aangeteken toe dit uitgegaan het nie.',
  'inbox.message.template.buttons': 'Knoppies op hierdie boodskap',
  'inbox.message.template.header.IMAGE': 'Prent bo-aan',
  'inbox.message.template.header.VIDEO': 'Video bo-aan',
  'inbox.message.template.header.DOCUMENT': 'Lêer bo-aan',
  'inbox.message.template.header.LOCATION': 'Ligging bo-aan',
  'inbox.message.failed.WINDOW_CLOSED.next': 'Stuur eerder ’n goedgekeurde sjabloon. Sodra hulle antwoord, kan jy weer vir nog 24 uur vryelik skryf.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.next': 'Jy hoef niks by Meta te doen nie. Boodskappe gaan weer deur sodra ons dit reggemaak het.',
  'inbox.message.failed.UNDELIVERABLE.next': 'Kyk of die nommer reg is en of dit WhatsApp gebruik. As dit reg is, het die persoon dalk boodskappe van jou besigheid geblokkeer.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED.next': 'Maak Nommers oop en koppel hierdie nommer weer. As dit ’n kode vra, lei ons jou deur die verifikasie.',
  'inbox.message.failed.MEDIA_NOT_FETCHED.next': 'Stuur dit weer met die lêer hier opgelaai eerder as geskakel, of kyk of die skakel openbaar is en steeds werk.',
  'inbox.message.failed.META_REFUSED.next': 'Probeer oor ’n paar minute weer. As dit aanhou gebeur, sê vir ons en ons kyk daarna.',
  'inbox.details.title': 'Boodskapbesonderhede',
  'inbox.details.open': 'Wat met hierdie boodskap gebeur het',
  'inbox.details.close': 'Maak boodskapbesonderhede toe',
  'inbox.details.error': 'Die besonderhede van hierdie boodskap kon nie gelaai word nie.',
  'inbox.details.timeline': 'Wat gebeur het',
  'inbox.details.timeline.sent': 'Gestuur',
  'inbox.details.timeline.received': 'Ontvang',
  'inbox.details.timeline.delivered': 'Afgelewer',
  'inbox.details.timeline.read': 'Gelees',
  'inbox.details.timeline.failed': 'Nie afgelewer nie',
  'inbox.details.timeline.later': '{{duration}} later',
  'inbox.details.read.absent': 'Geen leesbewys nie. WhatsApp stuur net een wanneer die ontvanger leesbewyse aangeskakel het, so dit kan leeg bly selfs nadat hulle jou boodskap gelees het.',
  'inbox.details.delivered.pending': 'WhatsApp het aflewering nog nie bevestig nie.',
  'inbox.details.why': 'Hoekom dit nie aangekom het nie',
  'inbox.details.why.metaCode': 'WhatsApp-kode',
  'inbox.details.why.metaWords': 'WhatsApp het gesê',
  'inbox.details.cost': 'Wat dit gekos het',
  'inbox.details.cost.unknown': 'Ons kon nie die grootboek vir hierdie boodskap lees nie, so ons kan nie sê wat dit gekos het nie.',
  'inbox.details.cost.rate': 'Tarief',
  'inbox.details.cost.worked': 'Bereken as',
  'inbox.details.cost.workedValue': '{{units}} × {{rate}}',
  'inbox.details.cost.chargedAt': 'Gehef',
  'inbox.details.cost.refunded': '{{amount}} terugbetaal omdat WhatsApp hierdie boodskap nie afgelewer het nie.',
  'inbox.details.sent': 'Wat gestuur is',
  'inbox.details.template': 'Sjabloon',
  'inbox.details.language': 'Taal',
  'inbox.details.category': 'Kategorie',
  'inbox.details.wording': 'Bewoording',
  'inbox.details.wording.recovered': 'Herwin uit die sjabloon soos dit nou is, so dit kan verskil van wat gestuur is.',
  'inbox.details.wording.lost': 'Nie aangeteken toe dit gestuur is nie, en die sjabloon is nie meer op lêer nie.',
  'inbox.details.params': 'Waardes wat ingevul is',
  'inbox.details.param.header': 'Opskrif',
  'inbox.details.param.body': 'Boodskap',
  'inbox.details.param.button': 'Knoppie',
  'inbox.details.file.name': 'Lêernaam',
  'inbox.details.file.type': 'Lêertipe',
  'inbox.details.file.size': 'Lêergrootte',
  'inbox.details.where': 'Waarheen dit gegaan het',
  'inbox.details.whereFrom': 'Waarvandaan dit gekom het',
  'inbox.details.to': 'Aan',
  'inbox.details.from': 'Van',
  'inbox.details.sentBy': 'Gestuur deur',
  'inbox.details.sentBy.you': 'Jy',
  'inbox.details.sentBy.teammate': '’n Spanlid',
  'inbox.details.sentBy.bot': 'Jou kletsbot',
  'inbox.details.sentBy.broadcast': '’n Uitsending',
  'inbox.details.sentBy.platform': 'RepletoryReach',
  'inbox.details.support': 'Vir ondersteuning',
  'inbox.details.metaId.hint': 'Haal dit aan as jy WhatsApp oor hierdie boodskap vra.',
  'inbox.details.copy': 'Kopieer',
  'inbox.details.copied': 'Gekopieer',
  'inbox.channel.whatsapp': 'WhatsApp',
  'inbox.channel.messenger': 'Messenger',
  'inbox.channel.instagram': 'Instagram',
  'inbox.window.closed.body.messenger': 'Messenger lewer \'n antwoord net binne 24 uur ná die persoon se laaste boodskap, en daardie tyd is verby. Jou antwoord bereik hulle sodra hulle weer skryf.',
  'inbox.window.closed.body.instagram': 'Instagram lewer \'n antwoord net binne 24 uur ná die persoon se laaste boodskap, en daardie tyd is verby. Jou antwoord bereik hulle sodra hulle weer skryf.',
  'inbox.send.notMetered': 'Die boodskap is gestuur, maar ons kon nie opteken wat dit gekos het nie. Ondersteuning is ingelig; niks is dubbel gehef nie.',
  'inbox.sidebar.title': 'Gespreksinstellings',
  'inbox.sidebar.toggle': 'Instellings',
  'inbox.sidebar.close': 'Sluit gespreksinstellings',
  'inbox.sidebar.details': 'Besonderhede',
  'inbox.sidebar.shortcuts': 'Sleutelbordkortpaaie',
  'inbox.notes.visibility': 'Slegs sigbaar vir jou span',
  'inbox.lock.hint': 'Sluit hierdie gesprek sodat niemand anders antwoord terwyl jy dit doen nie',
  'inbox.shortcuts.send': 'Stuur die antwoord',
  'inbox.shortcuts.newline': 'Begin ’n nuwe reël',
  'inbox.shortcuts.canned': 'Maak blikantwoorde oop',
  'inbox.shortcuts.pick': 'Beweeg deur blikantwoorde',
  'inbox.shortcuts.insert': 'Voeg die gemerkte antwoord in',
  'inbox.shortcuts.dismiss': 'Sluit die lys blikantwoorde',
  'inbox.shortcuts.key.enter': 'Enter',
  'inbox.shortcuts.key.shiftEnter': 'Shift+Enter',
  'inbox.shortcuts.key.slash': '/',
  'inbox.shortcuts.key.arrows': '↑ ↓',
  'inbox.shortcuts.key.enterTab': 'Enter of Tab',
  'inbox.shortcuts.key.esc': 'Esc',
  'inbox.bot.label': 'Bot',
  'inbox.bot.paused': 'Bot onderbreek',
  'inbox.bot.active': 'Bot antwoord',
  'inbox.bot.resumesAt': 'Die bot antwoord weer vanaf {{time}}, tensy die span voor dan antwoord.',
  'inbox.bot.untilResolved': 'Die bot bly stil totdat hierdie gesprek opgelos is of jy dit hervat.',
  'inbox.bot.activeHint': 'Die bot antwoord nuwe boodskappe. As jy self antwoord, neem jy die gesprek oor: die bot bly dan stil totdat dit afgehandel of teruggegee word.',
  'inbox.bot.resume': 'Hervat bot',
  'inbox.bot.pause': 'Onderbreek bot',
  'inbox.bot.resumed': 'Die bot sal weer op hierdie gesprek antwoord.',
  'inbox.bot.pausedToast': 'Die bot is op hierdie gesprek onderbreek.',
  'inbox.bot.failed': 'Die bot kon nie op hierdie gesprek verander word nie.',
  'inbox.block.label': 'Kliënt',
  'inbox.block.blocked': 'Geblokkeer',
  'inbox.block.notBlocked': 'Nie geblokkeer nie',
  'inbox.block.action': 'Blokkeer kliënt',
  'inbox.block.unblock': 'Ontblokkeer',
  'inbox.block.title': 'Blokkeer hierdie kliënt?',
  'inbox.block.body': 'Die bot sal hulle nie antwoord nie en niemand in jou span kan aan hulle skryf totdat jy hulle ontblokkeer nie. Hulle boodskappe word steeds hier gehou.',
  'inbox.block.reason': 'Rede',
  'inbox.block.reasonHint': 'Vir jou span. Die kliënt sien dit nie.',
  'inbox.block.alsoWhatsapp': 'Blokkeer ook hierdie nommer op WhatsApp',
  'inbox.block.alsoWhatsappHint': 'Hulle sal glad nie jou nommer kan boodskap nie. WhatsApp blokkeer net iemand wat die afgelope 24 uur geskryf het.',
  'inbox.block.confirm': 'Blokkeer',
  'inbox.block.done': 'Die kliënt is geblokkeer.',
  'inbox.block.doneWhatsapp': 'Die kliënt is hier en op WhatsApp geblokkeer.',
  'inbox.block.whatsappFailed': 'Hier geblokkeer, maar WhatsApp het nie die nommer geblokkeer nie: {{error}}',
  'inbox.block.unblocked': 'Die kliënt is ontblokkeer.',
  'inbox.block.failed': 'Die kliënt kon nie geblokkeer of ontblokkeer word nie.',
  'inbox.block.bar': 'Jy het hierdie kliënt geblokkeer. Die bot antwoord hulle nie en antwoorde is afgeskakel.',
  'inbox.block.since': 'Geblokkeer {{time}}',
  'inbox.block.onWhatsapp': 'Ook op WhatsApp geblokkeer',
  'inbox.channel.voice': 'Telefoonoproep',
  'inbox.voice.readOnly.title': '’n Rekord van telefoonoproepe',
  'inbox.voice.readOnly.body': 'Niks kan van hier af gestuur word nie. Om te antwoord, bel die persoon terug van jou foon af, of skryf aan hulle op WhatsApp.',
  'inbox.voice.openCall': 'Maak die oproep oop',
  'inbox.channel.web': 'Webwerfklets',
  'inbox.takeover.takeOver': 'Neem oor',
  'inbox.takeover.handBack': 'Gee terug aan die bot',
  'inbox.takeover.handBackTo': 'Gee terug aan {{bot}}',
  'inbox.takeover.with': 'By {{name}}',
  'inbox.takeover.you': 'jou',
  'inbox.takeover.someone': '’n spanlid',
  'inbox.takeover.theBot': 'die bot',
  'inbox.takeover.done': 'Die gesprek is joune. Die kliënt is vertel dat jy aangesluit het, en die bot bly uit totdat jy dit teruggee.',
  'inbox.takeover.handedBack': 'Teruggegee aan {{bot}}. Die kliënt is ingelig, en die gesprek bly in jou tou.',
  'inbox.takeover.failed': 'Dit het nie gewerk nie. Probeer weer.',
  'inbox.takeover.noticeFailed': 'Die kliënt kon nie ingelig word nie: {{error}}',
  'inbox.channel.email': 'E-pos',
  'inbox.email.subjectLine': 'Onderwerp: {{subject}}',
  'inbox.send.ownAccountNotOnPlan': 'Jou plan sluit nie meer jou eie WhatsApp-rekening in nie. Skuif na Growth of Scale om van hierdie nommer af aan te hou stuur.',
  'inbox.send.dailyLimit': 'Hierdie nommer het WhatsApp se limiet van {{limit}} nuwe mense in 24 uur bereik. Mense wat vandag reeds \'n boodskap gekry het, kan steeds bereik word.',
  'inbox.send.dailyLimit.shared': 'Jy het jou deel van vandag se limiet op ons gedeelde WhatsApp-rekening bereik: {{share}} nuwe mense in 24 uur. Ongebruikte ruimte word ná 18:00 uitgedeel, en mense wat vandag reeds \'n boodskap gekry het, kan steeds bereik word.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.customer': 'Nie afgelewer nie: jou WhatsApp-rekening het geen werkende betaalmetode in Meta nie.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.customer.next': 'Voeg \'n betaalmetode by in Meta se betaalinstellings vir jou WhatsApp-rekening. Boodskappe gaan weer deur sodra Meta dit kan faktureer.',
  'inbox.team.label': 'Span',
  'inbox.team.none': 'Geen span',
  'inbox.team.former': 'ŉ Span wat verwyder is',
  'inbox.team.hint': 'Net hierdie span se mense kry hierdie gesprek.',
  'inbox.team.moved': 'Na {{name}} geskuif.',
  'inbox.team.cleared': 'Uit sy span gehaal.',
  'inbox.team.failed': 'Die span kon nie verander word nie.',
  'inbox.filters.team': 'Span',
  'inbox.filters.team.any': 'Enige span',
  'inbox.filters.team.none': 'Geen span',
  'inbox.book': 'Bespreek',
  'inbox.book.done': '{{number}} is bespreek vir {{when}}.',
  'inbox.ticket.create': 'Skep kaartjie',
  'inbox.ticket.created': 'Kaartjie {{number}} is vir hierdie gesprek geskep.',
  'inbox.ticket.chipHint': 'Gekoppelde kaartjie: {{subject}}',
  'inbox.ticket.chipLabel': 'Kaartjie {{number}}, {{status}}. Maak die kaartjie oop.',
  'inbox.available.label': 'Neem nuwe gesprekke',
  'inbox.available.hint': 'Gesprekke wat nog niemand het nie, word om die beurt verdeel onder die mense wat dit aan het, binne die gesprek se span as dit een het. As jy dit afskakel, hou jy die gesprekke wat jy reeds het.',
  'inbox.available.short': 'neem nuwe gesprekke',
  'inbox.moreActions': 'Meer aksies',
  'inbox.lock.menu': 'Sluit terwyl ek antwoord',
  'inbox.pageBroken.title': 'Antwoorde kan nie op hierdie bladsy gestuur word nie',
  'inbox.pageBroken.body': 'Facebook aanvaar nie meer die bladsy se koppeling nie. Hul boodskappe kom steeds hier in, maar niks wat jy stuur, bereik hulle voordat die bladsy weer onder Kanale gekoppel is nie.',
  'inbox.pageBroken.action': 'Koppel weer',
  'inbox.message.unsupported.title': 'WhatsApp het hierdie boodskap nie deurgestuur nie',
  'inbox.message.unsupported.body': 'Dit is ’n soort boodskap wat WhatsApp nog nie aan besigheidsagteware aflewer nie, soos ’n aanmeldkode met ’n kopieerknoppie, so ons het nooit ontvang wat dit gesê het nie. As dit ’n kode van Facebook of ’n ander diens was, vra dit eerder per SMS of e-pos.',
  'inbox.message.unsupported.preview': '’n Boodskap wat WhatsApp nie deurgestuur het nie',
  'inbox.pageBroken.manychat': 'ManyChat aanvaar nie meer die sleutel vir hierdie bladsy nie. Hul boodskappe kom steeds hier in, maar niks wat jy stuur, bereik hulle voordat ’n nuwe ManyChat-sleutel onder Kanale ingeplak is nie.',
  'inbox.mediaText.transcript': 'Transkripsie:',
  'inbox.mediaText.photo': 'Wat jou assistent gesien het:',
  'inbox.mediaText.document': 'Die dokument lees:',
  'inbox.attachment.kind.attachment': '’n Lêer deur ManyChat gestuur'
};

export default inbox;
